import { makeAutoObservable, runInAction } from "mobx";
import {
  IsBoolean,
  IsIn,
  IsNotEmpty,
  IsNumber,
  IsString,
  Max,
  Min,
  validate,
  type ValidationError,
} from "class-validator";
import {
  ApiError,
  superadminCreateHelpExecutor,
  superadminCreateHelpService,
  superadminDeleteHelpExecutor,
  superadminDeleteHelpService,
  superadminUpdateHelpExecutor,
  superadminUpdateHelpService,
} from "@pro-sto/api-client";
import type {
  CreateHelpExecutorDto,
  CreateHelpServiceDto,
  UpdateHelpExecutorDto,
  UpdateHelpServiceDto,
} from "@pro-sto/api-client";
import { toastStore } from "./ToastStore";
import { helpStore } from "./HelpStore";
import type {
  HelpExecutorProfile,
  HelpPricingMode,
  HelpService,
  HelpServiceKind,
} from "@/pages/HelpPage/types";
import {
  clampExperienceYears,
  slugHelpServiceCode,
  toHelpServiceWriteBody,
} from "@/pages/HelpPage/helpMappers";
import { HELP_SERVICE_KIND_COMING_SOON } from "@/pages/HelpPage/constants";
import { toHelpPhoneDigits, toStoredHelpPhone } from "@/pages/HelpPage/helpPhone";

export type HelpSettingsTab = "catalog" | "executors";
export type HelpSettingsDeleteKind = "service" | "executor";
export type HelpSettingsDiscardKind = "close" | "tab" | "back";

export interface HelpSettingsDeleteTarget {
  kind: HelpSettingsDeleteKind;
  uuid: string;
  label: string;
}

export interface HelpSettingsDiscardIntent {
  kind: HelpSettingsDiscardKind;
  tab?: HelpSettingsTab;
}

export interface HelpServiceFormState {
  kind: HelpServiceKind;
  code: string;
  title: string;
  description: string;
  unitPrice: number;
  unitLabel: string;
  pricingMode: HelpPricingMode;
  providerServiceId: string;
  enabled: boolean;
  comingSoon: boolean;
}

export interface HelpExecutorFormState {
  name: string;
  phone: string;
  vehicleModel: string;
  vehiclePlate: string;
  experienceYears: number;
  company: string;
  enabled: boolean;
}

const PRICING_MODES: HelpPricingMode[] = ["fixed", "per_unit", "flexible"];

class HelpServiceFormDto {
  @IsString()
  @IsNotEmpty({ message: "Название обязательно" })
  title = "";

  @IsString()
  description = "";

  @IsNumber({}, { message: "Укажите цену" })
  @Min(0, { message: "Цена не может быть отрицательной" })
  unitPrice = 0;

  @IsString()
  unitLabel = "";

  @IsIn(PRICING_MODES, { message: "Выберите тип цены" })
  pricingMode: HelpPricingMode = "fixed";

  @IsString()
  providerServiceId = "";

  @IsBoolean()
  enabled = true;

  @IsBoolean()
  comingSoon = false;
}

class HelpExecutorFormDto {
  @IsString()
  @IsNotEmpty({ message: "Имя обязательно" })
  name = "";

  @IsString()
  @IsNotEmpty({ message: "Укажите телефон полностью" })
  phone = "";

  @IsString()
  vehicleModel = "";

  @IsString()
  vehiclePlate = "";

  @IsNumber({}, { message: "Укажите стаж" })
  @Min(0, { message: "Стаж не может быть отрицательным" })
  @Max(60, { message: "Стаж не больше 60 лет" })
  experienceYears = 0;

  @IsString()
  company = "";

  @IsBoolean()
  enabled = true;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function extractApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = asRecord(err.body);
    const message = body?.["message"];
    if (typeof message === "string" && message.length > 0) return message;
    if (Array.isArray(message) && typeof message[0] === "string") {
      return message[0];
    }
  }
  return fallback;
}

function mapValidationErrors(errors: ValidationError[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const error of errors) {
    if (error.constraints) {
      result[error.property] = Object.values(error.constraints)[0] ?? "";
    }
  }
  return result;
}

const emptyServiceForm = (kind: HelpServiceKind): HelpServiceFormState => ({
  kind,
  code: "",
  title: "",
  description: "",
  unitPrice: 0,
  unitLabel: "заказ",
  pricingMode: "fixed",
  providerServiceId: "",
  enabled: true,
  comingSoon: false,
});

const emptyExecutorForm = (): HelpExecutorFormState => ({
  name: "",
  phone: "",
  vehicleModel: "",
  vehiclePlate: "",
  experienceYears: 0,
  company: "",
  enabled: true,
});

function serviceFromCatalog(service: HelpService): HelpServiceFormState {
  return {
    kind: service.kind === "addon" ? "tow" : service.kind,
    code: service.code,
    title: service.title,
    description: service.description,
    unitPrice: service.unitPrice,
    unitLabel: service.unitLabel,
    pricingMode: service.pricingMode,
    providerServiceId: service.providerServiceId,
    enabled: service.enabled,
    comingSoon: service.comingSoon,
  };
}

function executorFromProfile(
  executor: HelpExecutorProfile,
): HelpExecutorFormState {
  return {
    name: executor.name,
    phone: toStoredHelpPhone(executor.phone) || executor.phone,
    vehicleModel: executor.vehicleModel,
    vehiclePlate: executor.vehiclePlate,
    experienceYears: executor.experienceYears,
    company: executor.company,
    enabled: executor.enabled,
  };
}

function snapshotService(form: HelpServiceFormState): string {
  return JSON.stringify({
    title: form.title,
    description: form.description,
    unitPrice: form.unitPrice,
    unitLabel: form.unitLabel,
    pricingMode: form.pricingMode,
    providerServiceId: form.providerServiceId,
    enabled: form.enabled,
    comingSoon: form.comingSoon,
  });
}

function snapshotExecutor(form: HelpExecutorFormState): string {
  return JSON.stringify({
    name: form.name,
    phone: form.phone,
    vehicleModel: form.vehicleModel,
    vehiclePlate: form.vehiclePlate,
    experienceYears: form.experienceYears,
    company: form.company,
    enabled: form.enabled,
  });
}

class HelpSettingsStore {
  services: HelpService[] = [];
  executors: HelpExecutorProfile[] = [];
  isLoading = false;

  tab: HelpSettingsTab = "catalog";
  catalogKind: HelpServiceKind = "tow";

  serviceFormOpen = false;
  serviceEditingUuid: string | null = null;
  serviceForm: HelpServiceFormState = emptyServiceForm("tow");
  serviceFormErrors: Record<string, string> = {};
  serviceSubmitAttempted = false;
  serviceSubmitting = false;
  private serviceBaseline = "";

  executorFormOpen = false;
  executorEditingUuid: string | null = null;
  executorForm: HelpExecutorFormState = emptyExecutorForm();
  executorFormErrors: Record<string, string> = {};
  executorSubmitAttempted = false;
  executorSubmitting = false;
  private executorBaseline = "";

  deleteTarget: HelpSettingsDeleteTarget | null = null;
  isDeleting = false;

  discardIntent: HelpSettingsDiscardIntent | null = null;
  private pendingLeaveAction: (() => void) | null = null;

  private loadSessionId = 0;

  constructor() {
    makeAutoObservable(this);
  }

  private bumpLoadSession = (): number => {
    this.loadSessionId += 1;
    return this.loadSessionId;
  };

  private syncFromHelpCatalog = () => {
    this.services = helpStore.catalog.slice();
    this.executors = helpStore.executors.slice();
  };

  private refreshCatalog = async () => {
    await helpStore.loadCatalog();
    runInAction(() => {
      this.syncFromHelpCatalog();
    });
  };

  get isServiceEditing(): boolean {
    return this.serviceEditingUuid != null;
  }

  get isExecutorEditing(): boolean {
    return this.executorEditingUuid != null;
  }

  get isServiceDirty(): boolean {
    if (!this.serviceFormOpen) return false;
    return snapshotService(this.serviceForm) !== this.serviceBaseline;
  }

  get isExecutorDirty(): boolean {
    if (!this.executorFormOpen) return false;
    return snapshotExecutor(this.executorForm) !== this.executorBaseline;
  }

  get isDrawerDirty(): boolean {
    return this.isServiceDirty || this.isExecutorDirty;
  }

  get visibleServices(): HelpService[] {
    if (this.catalogKind === "tow") {
      return this.services.filter(
        (service) => service.kind === "tow" || service.kind === "addon",
      );
    }
    return this.services.filter((service) => service.kind === this.catalogKind);
  }

  get isCatalogKindComingSoon(): boolean {
    return HELP_SERVICE_KIND_COMING_SOON[this.catalogKind];
  }

  loadAll = async () => {
    const session = this.bumpLoadSession();
    this.isLoading = true;

    try {
      await helpStore.loadCatalog();
      if (this.loadSessionId !== session) return;

      runInAction(() => {
        if (this.loadSessionId !== session) return;
        this.syncFromHelpCatalog();
        this.isLoading = false;
      });
    } catch (error) {
      if (this.loadSessionId !== session) return;

      runInAction(() => {
        if (this.loadSessionId !== session) return;
        this.services = [];
        this.executors = [];
        this.isLoading = false;
      });
      toastStore.showError(
        extractApiError(error, "Не удалось загрузить настройки помощи"),
      );
    }
  };

  reset = () => {
    this.bumpLoadSession();
    this.services = [];
    this.executors = [];
    this.isLoading = false;
    this.tab = "catalog";
    this.catalogKind = "tow";
    this.closeServiceForm(true);
    this.closeExecutorForm(true);
    this.deleteTarget = null;
    this.isDeleting = false;
    this.discardIntent = null;
    this.pendingLeaveAction = null;
  };

  setCatalogKind = (kind: HelpServiceKind) => {
    this.catalogKind = kind;
  };

  requestTabChange = (tab: HelpSettingsTab) => {
    if (tab === this.tab) return;
    if (this.isDrawerDirty) {
      this.discardIntent = { kind: "tab", tab };
      return;
    }
    this.closeOpenForms();
    this.tab = tab;
  };

  requestCloseServiceForm = () => {
    if (this.isServiceDirty) {
      this.discardIntent = { kind: "close" };
      return;
    }
    this.closeServiceForm();
  };

  requestCloseExecutorForm = () => {
    if (this.isExecutorDirty) {
      this.discardIntent = { kind: "close" };
      return;
    }
    this.closeExecutorForm();
  };

  requestLeave = (onLeave: () => void) => {
    if (!this.isDrawerDirty) {
      this.closeOpenForms();
      onLeave();
      return;
    }
    this.pendingLeaveAction = onLeave;
    this.discardIntent = { kind: "back" };
  };

  dismissDiscard = () => {
    this.discardIntent = null;
    this.pendingLeaveAction = null;
  };

  confirmDiscard = (): boolean => {
    const intent = this.discardIntent;
    const pendingLeaveAction = this.pendingLeaveAction;
    this.discardIntent = null;
    this.pendingLeaveAction = null;
    if (!intent) return false;
    this.closeOpenForms();
    if (intent.kind === "tab" && intent.tab) {
      this.tab = intent.tab;
      return false;
    }
    if (intent.kind === "back") {
      pendingLeaveAction?.();
      return false;
    }
    return false;
  };

  openServiceCreate = () => {
    if (this.isCatalogKindComingSoon) return;
    this.serviceEditingUuid = null;
    this.serviceForm = emptyServiceForm(this.catalogKind);
    this.serviceFormErrors = {};
    this.serviceSubmitAttempted = false;
    this.serviceBaseline = snapshotService(this.serviceForm);
    this.serviceFormOpen = true;
  };

  openServiceEdit = (service: HelpService) => {
    this.serviceEditingUuid = service.uuid;
    this.serviceForm = serviceFromCatalog(service);
    this.serviceFormErrors = {};
    this.serviceSubmitAttempted = false;
    this.serviceBaseline = snapshotService(this.serviceForm);
    this.serviceFormOpen = true;
  };

  closeServiceForm = (force = false) => {
    if (!force && this.isServiceDirty) return;
    this.serviceFormOpen = false;
    this.serviceEditingUuid = null;
    this.serviceForm = emptyServiceForm(this.catalogKind);
    this.serviceFormErrors = {};
    this.serviceSubmitAttempted = false;
    this.serviceSubmitting = false;
    this.serviceBaseline = "";
  };

  setServiceField = <K extends keyof HelpServiceFormState>(
    field: K,
    value: HelpServiceFormState[K],
  ) => {
    this.serviceForm[field] = value;
  };

  submitService = async () => {
    this.serviceSubmitAttempted = true;
    const dto = Object.assign(new HelpServiceFormDto(), {
      ...this.serviceForm,
      title: this.serviceForm.title.trim(),
      description: this.serviceForm.description.trim(),
      unitLabel: this.serviceForm.unitLabel.trim(),
      providerServiceId: this.serviceForm.providerServiceId.trim(),
    });
    const errors = await validate(dto);
    if (errors.length > 0) {
      runInAction(() => {
        this.serviceFormErrors = mapValidationErrors(errors);
      });
      return;
    }

    const kind = this.serviceEditingUuid
      ? this.services.find((item) => item.uuid === this.serviceEditingUuid)
          ?.kind ?? this.serviceForm.kind
      : this.serviceForm.kind;
    const code = this.serviceEditingUuid
      ? this.serviceForm.code || slugHelpServiceCode(dto.title, "service")
      : slugHelpServiceCode(dto.title, "service");

    const body = toHelpServiceWriteBody({
      kind,
      code,
      title: dto.title,
      description: dto.description,
      unitPrice: dto.unitPrice,
      pricingMode: dto.pricingMode,
      unitLabel: dto.unitLabel,
      enabled: dto.enabled,
      comingSoon: dto.comingSoon,
      providerServiceId: dto.providerServiceId,
    });

    this.serviceSubmitting = true;
    try {
      if (this.serviceEditingUuid) {
        await superadminUpdateHelpService({
          uuid: this.serviceEditingUuid,
          requestBody: body as UpdateHelpServiceDto,
        });
        toastStore.showSuccess("Услуга обновлена");
      } else {
        await superadminCreateHelpService({
          requestBody: body as CreateHelpServiceDto,
        });
        toastStore.showSuccess("Услуга добавлена");
      }

      await this.refreshCatalog();
      runInAction(() => {
        this.serviceSubmitting = false;
      });
      this.closeServiceForm(true);
    } catch (error) {
      runInAction(() => {
        this.serviceSubmitting = false;
      });
      toastStore.showError(
        extractApiError(error, "Не удалось сохранить услугу"),
      );
    }
  };

  openExecutorCreate = () => {
    this.executorEditingUuid = null;
    this.executorForm = emptyExecutorForm();
    this.executorFormErrors = {};
    this.executorSubmitAttempted = false;
    this.executorBaseline = snapshotExecutor(this.executorForm);
    this.executorFormOpen = true;
  };

  openExecutorEdit = (executor: HelpExecutorProfile) => {
    this.executorEditingUuid = executor.uuid;
    this.executorForm = executorFromProfile(executor);
    this.executorFormErrors = {};
    this.executorSubmitAttempted = false;
    this.executorBaseline = snapshotExecutor(this.executorForm);
    this.executorFormOpen = true;
  };

  closeExecutorForm = (force = false) => {
    if (!force && this.isExecutorDirty) return;
    this.executorFormOpen = false;
    this.executorEditingUuid = null;
    this.executorForm = emptyExecutorForm();
    this.executorFormErrors = {};
    this.executorSubmitAttempted = false;
    this.executorSubmitting = false;
    this.executorBaseline = "";
  };

  setExecutorField = <K extends keyof HelpExecutorFormState>(
    field: K,
    value: HelpExecutorFormState[K],
  ) => {
    this.executorForm[field] = value;
  };

  submitExecutor = async () => {
    this.executorSubmitAttempted = true;
    const normalizedPhone = toStoredHelpPhone(this.executorForm.phone);
    const dto = Object.assign(new HelpExecutorFormDto(), {
      ...this.executorForm,
      name: this.executorForm.name.trim(),
      vehicleModel: this.executorForm.vehicleModel.trim(),
      vehiclePlate: this.executorForm.vehiclePlate.trim(),
      company: this.executorForm.company.trim(),
      experienceYears: clampExperienceYears(this.executorForm.experienceYears),
      phone: normalizedPhone,
    });

    const errors = await validate(dto);
    const formErrors = mapValidationErrors(errors);

    if (!normalizedPhone || toHelpPhoneDigits(normalizedPhone).length !== 10) {
      formErrors.phone = "Укажите телефон полностью";
    }

    if (Object.keys(formErrors).length > 0) {
      runInAction(() => {
        this.executorFormErrors = formErrors;
      });
      return;
    }

    const requestBody: CreateHelpExecutorDto = {
      name: dto.name,
      phone: dto.phone,
      vehicle_model: dto.vehicleModel,
      vehicle_plate: dto.vehiclePlate,
      experience_years: dto.experienceYears,
      company: dto.company,
      enabled: dto.enabled,
    };

    this.executorSubmitting = true;
    try {
      if (this.executorEditingUuid) {
        await superadminUpdateHelpExecutor({
          uuid: this.executorEditingUuid,
          requestBody: requestBody as UpdateHelpExecutorDto,
        });
        toastStore.showSuccess("Исполнитель обновлён");
      } else {
        await superadminCreateHelpExecutor({ requestBody });
        toastStore.showSuccess("Исполнитель добавлен");
      }

      await this.refreshCatalog();
      runInAction(() => {
        this.executorSubmitting = false;
      });
      this.closeExecutorForm(true);
    } catch (error) {
      runInAction(() => {
        this.executorSubmitting = false;
      });
      toastStore.showError(
        extractApiError(error, "Не удалось сохранить исполнителя"),
      );
    }
  };

  requestDelete = (target: HelpSettingsDeleteTarget) => {
    this.deleteTarget = target;
  };

  cancelDelete = () => {
    this.deleteTarget = null;
  };

  confirmDelete = async () => {
    if (!this.deleteTarget) return;
    this.isDeleting = true;
    const { kind, uuid } = this.deleteTarget;

    try {
      if (kind === "service") {
        await superadminDeleteHelpService({ uuid });
        toastStore.showSuccess("Услуга удалена");
      } else {
        await superadminDeleteHelpExecutor({ uuid });
        toastStore.showSuccess("Исполнитель удалён");
      }

      await this.refreshCatalog();
      runInAction(() => {
        this.isDeleting = false;
        this.deleteTarget = null;
      });
    } catch (error) {
      runInAction(() => {
        this.isDeleting = false;
      });
      toastStore.showError(extractApiError(error, "Не удалось удалить"));
    }
  };

  private closeOpenForms = () => {
    this.closeServiceForm(true);
    this.closeExecutorForm(true);
  };
}

export const helpSettingsStore = new HelpSettingsStore();
export type { HelpSettingsStore };
